/**
 * 소리와 진동 — 파일 없이 브라우저가 그 자리에서 만든다(작업지시서 5절 · 6절).
 *
 * 🔇 처음엔 꺼져 있다 — 화장실에서 하는 게임이라 갑자기 소리가 나면 곤란하다. 켜면 이 브라우저가 기억한다.
 * 📳 진동은 소리를 켠 사람에게만 준다(안드로이드 · navigator.vibrate). 아이폰 사파리는 진동이 없다.
 */
const KEY = 'pop.alchemist.sound.v1';

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    try {
        ctx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        if (ctx.state === 'suspended') void ctx.resume();
        return ctx;
    } catch {
        return null;
    }
}

function tone(freq: number, start: number, length: number, type: OscillatorType = 'sine', volume = 0.15) {
    const a = audio();
    if (!a) return;
    const osc = a.createOscillator();
    const gain = a.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, a.currentTime + start);
    gain.gain.setValueAtTime(0.0001, a.currentTime + start);
    gain.gain.exponentialRampToValueAtTime(volume, a.currentTime + start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + start + length);
    osc.connect(gain).connect(a.destination);
    osc.start(a.currentTime + start);
    osc.stop(a.currentTime + start + length + 0.02);
}

class Sound {
    on = $state(false);

    constructor() {
        try {
            this.on = localStorage.getItem(KEY) === 'on';
        } catch {
            this.on = false;
        }
    }

    toggle(): void {
        this.on = !this.on;
        try {
            localStorage.setItem(KEY, this.on ? 'on' : 'off');
        } catch {
            // 기억만 못 할 뿐이다
        }
        if (this.on) {
            audio(); // 누른 그 순간에 열어야 브라우저가 소리를 허락한다
            this.pick();
        }
    }

    private buzz(pattern: number[]): void {
        if (!this.on) return;
        try {
            navigator.vibrate?.(pattern);
        } catch {
            // 진동이 없는 기기
        }
    }

    /** 재료를 솥에 넣을 때 — 톡 */
    pick(): void {
        if (!this.on) return;
        tone(880, 0, 0.06, 'triangle', 0.08);
    }

    /** 불 때는 동안 — 보글보글 */
    bubble(ms: number): void {
        if (!this.on) return;
        for (let t = 0; t < ms / 1000; t += 0.09 + Math.random() * 0.06) {
            tone(280 + Math.random() * 420, t, 0.07, 'sine', 0.06);
        }
    }

    /** 성공 — 띠링. 등급이 높을수록 음이 많고 높다 */
    chime(grade: number): void {
        if (!this.on) return;
        const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5, 1567.98];
        const count = 2 + grade;
        for (let i = 0; i < count; i++) tone(notes[Math.min(i, notes.length - 1)], i * 0.08, 0.35, 'triangle', 0.12);
        if (grade >= 4) for (let i = 0; i < 6; i++) tone(2093 + i * 220, 0.5 + i * 0.05, 0.25, 'sine', 0.05); // 전설 — 반짝임
        this.buzz(grade >= 4 ? [60, 40, 60, 40, 140] : grade >= 3 ? [30, 40, 30] : [20]);
    }

    /** 펑 — 잡음 한 덩이와 낮은 쿵 */
    boom(): void {
        if (!this.on) return;
        const a = audio();
        if (a) {
            const length = 0.5;
            const buffer = a.createBuffer(1, Math.floor(a.sampleRate * length), a.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.5);
            const src = a.createBufferSource();
            const filter = a.createBiquadFilter();
            const gain = a.createGain();
            src.buffer = buffer;
            filter.type = 'lowpass';
            filter.frequency.value = 900;
            gain.gain.value = 0.35;
            src.connect(filter).connect(gain).connect(a.destination);
            src.start();
            tone(70, 0, 0.35, 'sine', 0.3);
        }
        this.buzz([80, 30, 80]);
    }
}

export const sound = new Sound();
